import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Container, Paper, Typography, TextField, Button, Alert, Box } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { validatePasswordStrength, PASSWORD_REQUIREMENTS_MESSAGE } from '../../utils/password';

const useQuery = () => {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
};

const ResetPasswordPage = () => {
  const query = useQuery();
  const uid = query.get('uid') || '';
  const token = query.get('token') || '';
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!uid || !token) {
      setError('Invalid or expired password reset link.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    const validation = validatePasswordStrength(password);
    if (!validation.valid) {
      setPasswordError(validation.message);
      setError(validation.message);
      return;
    }
    setPasswordError('');
    setError('');
    setLoading(true);
    // const result = await resetPassword({ uid, token, password, confirmPassword });
    const result = await resetPassword({
  uid,
  token,
  password,
  confirmPassword,   // MUST match backend key
});
    if (result?.success) {
      setMessage('Password updated successfully. You can now log in.');
      setPassword('');
      setConfirmPassword('');
    } else {
      setError(result?.message || 'Unable to reset password.');
    }
    setLoading(false);
  };

  return (
    <Container maxWidth="xs">
      <Paper sx={{ p: 3, mt: 8 }}>
        <Typography variant="h5" gutterBottom>
          Reset Password
        </Typography>
        {!uid || !token ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            The password reset link is invalid or has expired.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Choose a new password for your Delight Supported Living account.
            </Typography>
            {message && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {message}
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                error={Boolean(passwordError)}
                helperText={passwordError || PASSWORD_REQUIREMENTS_MESSAGE}
              />
              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                required
              />
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={loading} sx={{ mt: 2 }}>
                {loading ? 'Saving...' : 'Update Password'}
              </Button>
              <Button fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')}>
                Back to Login
              </Button>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default ResetPasswordPage;
